/**
 * The last plan this browser saw for this person, for the first second of a load.
 *
 * Every API call waits for the token, and the plan read goes out with them — so
 * until it lands the load profile would otherwise default to `standard`.
 * A customer using `lean` would fetch 50 Transactions, then fetch 25 again
 * once the plan arrived. While the read is pending, the last answer supplies
 * the load profile; the verified read replaces it when it lands.
 *
 * Hints retain layout and load budgets only. DashboardPlan withholds feature
 * grants until the current read is verified, even when this hook supplies a
 * paid hint. Streaming also waits for the current verified read.
 * Scoped to the signed-in user, so a shared browser never shows one person's
 * plan to the next.
 */
import * as React from "react";
import type { Plan } from "@authe/fe-commons/plans";
import { createScopedStorage } from "@authe/fe-commons/storage";
import { isRecord } from "@/api/wire";
import type { PlanReadStatus } from "./currentPlan";
import { useStorageIdentity } from "./storageIdentity";

const PLAN_HINT_NAME = "plan.hint";
/** Bump when the stored shape changes: an older hint is then ignored, not misread. */
const PLAN_HINT_VERSION = 1;

/** What `<PlanProvider>` is given — the only part of the read a hint stands in for. */
export interface PlanInputs {
  plan: Plan | null;
  overrides: Record<string, unknown>;
  metadata: Record<string, unknown>;
}

/** A stored hint, or `null` for anything that is not one this build wrote. */
export function planHintFrom(raw: unknown): PlanInputs | null {
  if (!isRecord(raw) || raw.v !== PLAN_HINT_VERSION) return null;
  const plan = raw.plan;
  if (!isRecord(plan) || typeof plan.id !== "string" || !Array.isArray(plan.entitlements) || !Array.isArray(plan.quotas)) {
    return null;
  }
  return {
    plan: plan as unknown as Plan,
    overrides: isRecord(raw.overrides) ? raw.overrides : {},
    metadata: isRecord(raw.metadata) ? raw.metadata : {},
  };
}

/**
 * `current` once the read has answered; until then the last answer this user
 * got, when there is one.
 *
 * A settled read with a plan is remembered; a settled read without one (failed,
 * or a tier nobody declares) forgets it, so the next load starts from unknown
 * rather than from a plan the API has stopped confirming.
 */
export function usePlanWithHint(current: PlanInputs & { status: PlanReadStatus }): PlanInputs {
  const { app, customerId, orgId, tenantId, userId } = useStorageIdentity();
  const scope = React.useMemo(
    () => (userId ? createScopedStorage({ app, customerId, orgId, tenantId, userId }) : null),
    [app, customerId, orgId, tenantId, userId],
  );
  // Read once per identity: after the first answer it is never consulted again.
  const hint = React.useMemo(() => (scope ? planHintFrom(scope.getAt(PLAN_HINT_NAME, "user", null)) : null), [scope]);

  const { plan, overrides, metadata, status } = current;
  React.useEffect(() => {
    if (!scope || status !== "settled") return;
    if (plan) scope.set(PLAN_HINT_NAME, { v: PLAN_HINT_VERSION, plan, overrides, metadata }, "user");
    else scope.remove(PLAN_HINT_NAME, "user");
  }, [scope, status, plan, overrides, metadata]);

  return React.useMemo(
    () => (status === "pending" && hint ? hint : { plan, overrides, metadata }),
    [status, hint, plan, overrides, metadata],
  );
}
